import type { SpeechRequest } from '@/api/types';
import { SAY_TEXT_VOICES } from '@/flow/config/sayTextVoice';
import defaultPhrases from './offlinePhrases.json';

export const OFFLINE_SPEEDS = [0.5, 1, 2] as const;
export const OFFLINE_PITCHES = [-0.15, 0, 0.15] as const;
export type OfflinePhrase = { id: string; text: string };
export type OfflineAudioVoice = { speaker: number; speedScale: number; pitchScale: number };
export const OFFLINE_AUDIO_VARIANTS: OfflineAudioVoice[] = SAY_TEXT_VOICES.flatMap(({ speaker }) =>
  OFFLINE_SPEEDS.flatMap((speedScale) => OFFLINE_PITCHES.map((pitchScale) => ({ speaker, speedScale, pitchScale }))));

export function offlineAudioVariantKey(phrase: OfflinePhrase, voice: OfflineAudioVoice): string {
  return JSON.stringify([phrase.id, phrase.text.trim(), voice.speaker, voice.speedScale, voice.pitchScale]);
}

export function createOfflinePhraseId(existingIds: Iterable<string> = []): string {
  const used = new Set(existingIds);
  for (let attempt = 0; attempt < 64; attempt++) {
    const id = crypto.randomUUID().slice(0, 8);
    if (!used.has(id)) return id;
  }
  throw new Error('文章IDを作成できません。もう一度お試しください。');
}

export function normalizeOfflinePhraseDraft(phrases: OfflinePhrase[], selectedIds: string[]) {
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const used = new Set<string>();
  const normalized = phrases.map((phrase) => {
    const existing = defaultPhrases.find(({ text }) => text === phrase.text.trim());
    const previousId = phrase.id.replace(/^phrase-/, '');
    const candidate = existing?.id ?? (/^[0-9a-f]{8}$/i.test(previousId) || uuid.test(previousId) ? previousId.slice(0, 8).toLowerCase() : null);
    const id = candidate && !used.has(candidate) ? candidate : createOfflinePhraseId(used);
    used.add(id);
    return { ...phrase, id };
  });
  return { phrases: normalized, selectedIds: normalized.filter((_, index) => selectedIds.includes(phrases[index].id)).map(({ id }) => id) };
}

export function offlineAudioFilename(id: string, request: SpeechRequest): string {
  const speaker = request.speaker ?? 1;
  const speed = request.speedScale ?? 1;
  const pitch = request.pitchScale ?? 0;
  return speaker === 1 && speed === 1 && pitch === 0
    ? `${id}.wav`
    : `${id}-s${speaker}-v${speed}-p${pitch}.wav`;
}

export function validateOfflinePhrases(phrases: OfflinePhrase[]): OfflinePhrase[] {
  if (!phrases.length) throw new Error('文章を1件以上追加してください。');
  const normalized = phrases.map(({ id, text }) => ({ id, text: text.trim() }));
  if (normalized.some(({ id, text }) => !/^[a-zA-Z0-9_-]+$/.test(id) || !text || [...text].length > 200)) {
    throw new Error('文章は1〜200文字で入力してください。');
  }
  if (new Set(normalized.map(({ id }) => id)).size !== normalized.length
    || new Set(normalized.map(({ text }) => text)).size !== normalized.length) {
    throw new Error('重複する文章を削除してください。');
  }
  return normalized;
}

export function isOfflineWav(audio: ArrayBuffer): boolean {
  const header = new TextDecoder('ascii').decode(audio.slice(0, 12));
  return audio.byteLength > 44 && header.startsWith('RIFF') && header.slice(8) === 'WAVE';
}

export async function generateOfflineAudioPackage(
  phrases: OfflinePhrase[],
  synthesize: (request: SpeechRequest, signal?: AbortSignal) => Promise<ArrayBuffer>,
  signal: AbortSignal,
  onProgress: (completed: number, total: number) => void,
  voices: readonly OfflineAudioVoice[] = OFFLINE_AUDIO_VARIANTS,
): Promise<Record<string, Uint8Array>> {
  const selected = validateOfflinePhrases(phrases);
  const files: Record<string, Uint8Array> = {};
  const variants: Array<SpeechRequest & { file: string; phraseId: string }> = [];
  const total = selected.length * voices.length;
  onProgress(0, total);
  for (const phrase of selected) {
    for (const voice of voices) {
      signal.throwIfAborted();
      const request = { text: phrase.text, ...voice };
      const audio = await synthesize(request, signal);
      signal.throwIfAborted();
      if (!isOfflineWav(audio)) throw new Error(`音声データが不正です: ${phrase.text}`);
      const file = offlineAudioFilename(phrase.id, request);
      files[`audio/offline/${file}`] = new Uint8Array(audio);
      variants.push({ ...request, file, phraseId: phrase.id });
      onProgress(variants.length, total);
    }
  }
  const encode = (value: unknown) => new TextEncoder().encode(JSON.stringify(value, null, 2));
  files['offlinePhrases.json'] = encode(selected);
  files['audio/offline/manifest.json'] = encode({ version: 1, phrases: selected, variants });
  return files;
}